import { useState, useEffect } from "react";
import dayjs from "dayjs";

import { Grid, Button, Tab, Tabs, Tooltip, IconButton } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

import { Select, ToggleButtons } from "@aclymatepackages/atoms";
import { formatDecimal } from "@aclymatepackages/formatters";
import {
  addDataRow,
  editRowData,
  removeDataRow
} from "@aclymatepackages/array-immutability-helpers";

import DatePicker from "../../atoms/mui/DatePicker";
import AirportAutocomplete from "../autocomplete/AirportAutocomplete";
import Routes from "../Routes";

import { isObjectEmpty } from "../../../helpers/otherHelpers";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import useAccountingData from "../../../helpers/hooks/accountingData";
import useInputMeasurementSystem from "../../../helpers/hooks/inputMeasurementSystem";
import { buildNewConnectedLegs } from "../../../helpers/components/inputs";

const fetchGcdAndCarbon = async (to, from, passClass = "economy") => {
  if (!isObjectEmpty(to) && !isObjectEmpty(from)) {
    return await fetchOurApi({
      path: "/calcs/travel/fetch-gcd-and-carbon",
      method: "POST",
      data: {
        to,
        from,
        passClass
      },
      callback: (res) => res
    });
  }
  return {};
};

const fetchFlightsCarbon = async (distance, passClass = "economy") => {
  if (distance) {
    return await fetchOurApi({
      path: "/calcs/travel/fetch-flights-carbon",
      method: "POST",
      data: {
        distance,
        passClass
      },
      callback: (res) => res
    });
  }
  return {};
};

const allClasses = ["Economy", "Premium", "Business", "First"];

const classOptions = (distance, defaultClass = null) => {
  if (!distance && defaultClass) {
    return allClasses;
  }
  if (distance) {
    return distance >= 3700
      ? allClasses
      : distance < 3700 && distance >= 500
        ? ["Economy", "First"]
        : ["Economy"];
  }
  return ["Economy"];
};

const basePassClassObj = { passClass: "economy" };
const buildFlightObj = (flightData) => ({
  route: flightData || {
    destinations: [{}, {}],
    legs: [basePassClassObj]
  }
});

const makeFlightArrays = () => {
  const firstFlight = buildFlightObj({
    destinations: [{}, {}],
    legs: [basePassClassObj]
  });

  const roundTripFlights = [
    firstFlight,
    buildFlightObj({
      destinations: [{}, {}],
      legs: [basePassClassObj]
    })
  ];

  const oneWayFlight = [firstFlight];
  const multiCityFlights = [firstFlight, buildFlightObj(), buildFlightObj()];
  return { roundTripFlights, oneWayFlight, multiCityFlights };
};

const totalFlightCarbonTons = (flights) =>
  flights
    .map((flight) => flight.route.legs.map((leg) => leg.tonsCo2e || 0))
    .flat()
    .reduce((acc, curr) => acc + curr, 0);

const LegRow = ({
  leg,
  editLeg: setPassClass,
  defaultClass,
  setDefaultClass
}) => {
  const { distance, passClass, tonsCo2e } = leg;

  const { convertCarbonUnits, carbonUnitLabel } = useInputMeasurementSystem();

  const editPassClass = (passClass) => {
    setPassClass(passClass);
    return setDefaultClass(passClass);
  };

  return (
    <Select
      size="small"
      label="Passenger Class"
      value={passClass}
      editValue={editPassClass}
      disabled={!distance}
      options={classOptions(distance, defaultClass).map((option) => ({
        label: option,
        value: option.toLowerCase()
      }))}
      helperText={
        tonsCo2e &&
        `${formatDecimal(convertCarbonUnits(tonsCo2e))} ${carbonUnitLabel} CO2`
      }
    />
  );
};

const FlightsAirportAutocomplete = ({
  destination,
  editDestination,
  label
}) => (
  <AirportAutocomplete
    label={label}
    airport={destination || ""}
    editValue={editDestination}
    isInternational
  />
);

const FlightBlock = ({
  defaultClass,
  setDefaultClass,
  flightIdx,
  flight,
  editFlight,
  editMultiFlightDestinations,
  flights,
  tripType,
  buildLeg,
  findNewLegs
}) => {
  const { route, date } = flight || {};

  const [{ earliestNewEmissionDate, startDate }] = useAccountingData();

  const editRoute = (updateObj) =>
    editFlight("route", {
      ...route,
      ...updateObj
    });

  const setPassClass = async (passClass, legIdx) => {
    const { legs } = route;

    return await Promise.all(
      legs.map(async (leg, idx) => {
        if (idx === legIdx && leg.distance) {
          const { flightCarbonTons } = await fetchFlightsCarbon(
            leg.distance,
            passClass
          );
          return {
            ...leg,
            passClass,
            tonsCo2e: flightCarbonTons || leg.tonsCo2e
          };
        }

        if (idx === legIdx) {
          return { ...leg, passClass };
        }

        return leg;
      })
    );
  };

  const minDate = () => {
    if (tripType === "round-trip" && flightIdx === 1 && flights[0]?.date) {
      return flights[0].date;
    }
    if (tripType === "multi-city" && flights[flightIdx - 1]?.date) {
      return flights[flightIdx - 1].date;
    }

    if (earliestNewEmissionDate) {
      return earliestNewEmissionDate;
    }

    return startDate;
  };

  const maxDate = () => {
    if (tripType === "round-trip" && flightIdx === 0 && flights[1]?.date) {
      return flights[1].date;
    }
    if (tripType === "multi-city" && flights[flightIdx + 1]?.date) {
      return flights[flightIdx + 1].date;
    }
    return dayjs().add(10, "year");
  };

  return (
    <Grid container spacing={3} direction="column">
      <Grid item container justifyContent="center">
        <Grid item sm={7}>
          <DatePicker
            label="Date of Travel"
            date={date || null}
            editDate={(date) => editFlight("date", new Date(date))}
            size="small"
            minDate={minDate() || undefined}
            minDateMessage="This flight must be after the last one."
            maxDate={maxDate() || undefined}
            maxDateMessage="This flight must be before the last one."
            id={`flight${flightIdx}-date-input`}
          />
        </Grid>
      </Grid>
      <Grid item>
        <Routes
          route={route}
          editRoute={editRoute}
          flightIdx={flightIdx}
          DestinationRowComponent={FlightsAirportAutocomplete}
          LegColumnComponent={LegRow}
          onEditLeg={setPassClass}
          legColumnComponentProps={{
            defaultClass,
            setDefaultClass
          }}
          onEditDestination={editMultiFlightDestinations}
          buildLeg={buildLeg}
          findNewLegs={findNewLegs}
        />
      </Grid>
    </Grid>
  );
};

//TODO: There's a bug in how the name of trip are saved when they're input manually
//Need to save it with a name that reflects the destination
//Or at the very least, the date of the flight, and not just the date it was entered
const FlightsInput = ({
  transaction: { status },
  tripType,
  setTripType,
  flights,
  setFlights,
  activeFlightIdx,
  setActiveFlightIdx
}) => {
  const [defaultClass, setDefaultClass] = useState("economy");

  const disabled = status === "locked";

  const editFlights = (flightIdx) => (field) => (value) =>
    editRowData(flightIdx, setFlights)(field, value);

  const changeTripType = (newTripType) => {
    setTripType(newTripType);
    setActiveFlightIdx(0);
    switch (newTripType) {
      case "one-way":
        setFlights(makeFlightArrays().oneWayFlight);
        break;
      case "multi-city":
        setFlights(makeFlightArrays().multiCityFlights);
        break;
      default:
        setFlights(makeFlightArrays().roundTripFlights);
    }
  };

  const findNewPassClass = (gcd, passClass) => {
    const availablePassClasses = classOptions(gcd).map((passClass) =>
      passClass.toLowerCase()
    );

    if (availablePassClasses.length === 1) {
      return availablePassClasses[0];
    }

    if (defaultClass && availablePassClasses.includes(defaultClass)) {
      return defaultClass;
    }

    return passClass;
  };

  const findNewLegs = async ({ legs, currentLeg, from, to }) => {
    const { passClass } = currentLeg;
    const { gcd, flightCarbonTons } = await fetchGcdAndCarbon(
      from,
      to,
      passClass
    );

    return legs.map((leg, idx) =>
      !idx
        ? {
            ...leg,
            distance: gcd,
            tonsCo2e: flightCarbonTons,
            passClass: findNewPassClass(gcd, passClass)
          }
        : leg
    );
  };

  const buildLeg = async (from, to) => {
    const { gcd, flightCarbonTons } = await fetchGcdAndCarbon(
      from,
      to,
      defaultClass
    );
    return {
      from,
      to,
      distance: gcd,
      tonsCo2e: flightCarbonTons,
      passClass: findNewPassClass(gcd, defaultClass)
    };
  };

  const editMultiFlightDestinations =
    (flightIdx) => async (airport, destinationIdx) => {
      const editConnectedFlight = async (
        flightIdx,
        destinationIdx,
        airport
      ) => {
        const { destinations, legs } = flights[flightIdx].route || {};

        const newDestinations = destinations.map((destination, idx) =>
          idx === destinationIdx ? airport : destination
        );
        const newLegs = await buildNewConnectedLegs({
          destinationIdx,
          destinations: newDestinations,
          legs,
          findNewLegs,
          buildLeg
        });

        return editFlights(flightIdx)("route")({
          destinations: newDestinations,
          legs: newLegs
        });
      };

      const { destinations = [] } = flights[flightIdx].route || {};

      if (tripType === "one-way") {
        return;
      }

      const editOtherRoundTripFlights = () => {
        if (flightIdx === 0 && destinationIdx === 0) {
          const { destinations: secondLegDestinations } =
            flights[1].route || {};
          return editConnectedFlight(
            1,
            secondLegDestinations.length - 1,
            airport
          );
        }
        if (flightIdx === 1 && destinationIdx === destinations.length - 1) {
          return editConnectedFlight(0, 0, airport);
        }

        return;
      };

      if (flightIdx === 0 && destinationIdx === 0) {
        return editOtherRoundTripFlights();
      }

      if (
        flightIdx < flights.length - 1 &&
        destinationIdx === destinations.length - 1
      ) {
        return editConnectedFlight(flightIdx + 1, 0, airport);
      }

      if (flightIdx !== 0 && destinationIdx === 0) {
        const prevFlightDestinationsCount =
          flights[flightIdx - 1].route?.destinations?.length;

        return editConnectedFlight(
          flightIdx - 1,
          prevFlightDestinationsCount - 1,
          airport
        );
      }
    };

  return (
    <Grid item container direction="column" spacing={2} wrap="nowrap">
      <Grid container item justifyContent="center">
        <Grid item>
          <ToggleButtons
            size="small"
            value={tripType}
            onChange={changeTripType}
            buttons={[
              {
                value: "round-trip",
                name: "Round Trip",
                id: "round-trip-option"
              },
              { value: "one-way", name: "One Way", id: "one-way-option" },
              {
                value: "multi-city",
                name: "Multi-City",
                id: "multi-city-option"
              }
            ]}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Grid item sm={12}>
        <Tabs
          value={activeFlightIdx}
          onChange={(_, value) => setActiveFlightIdx(value)}
          variant={tripType === "multi-city" ? "scrollable" : "fullWidth"}
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="inherit"
        >
          {flights.map((_, idx) => (
            <Tab
              key={`flight-tab-${idx}`}
              value={idx}
              label={
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    {tripType === "one-way"
                      ? "One Way Trip"
                      : tripType === "round-trip"
                        ? idx === 1
                          ? "Return Trip"
                          : "Outgoing Trip"
                        : `Trip ${idx + 1}`}
                  </Grid>
                  {tripType === "multi-city" && flights.length > 2 && !!idx && (
                    <Grid item>
                      <Tooltip title="Remove Flight">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveFlightIdx((currentIdx) =>
                              currentIdx ? currentIdx - 1 : 0
                            );
                            return removeDataRow(idx, setFlights)();
                          }}
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              }
              id={`flight-tab-${idx}`}
            />
          ))}
        </Tabs>
        <Grid item style={{ paddingTop: "16px" }}>
          <FlightBlock
            key={`flight-row-${activeFlightIdx}`}
            defaultClass={defaultClass}
            setDefaultClass={setDefaultClass}
            transactionStatus={status}
            flights={flights}
            flightIdx={activeFlightIdx}
            flight={flights[activeFlightIdx]}
            editFlight={editRowData(activeFlightIdx, setFlights)}
            editMultiFlightDestinations={editMultiFlightDestinations(
              activeFlightIdx
            )}
            tripType={tripType}
            removeFlight={removeDataRow(activeFlightIdx, setFlights)}
            buildLeg={buildLeg}
            findNewLegs={findNewLegs}
          />
        </Grid>
      </Grid>
      {tripType === "multi-city" && (
        <Grid item container sm={12} justifyContent="center">
          <Grid item>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => addDataRow(setFlights, buildFlightObj())}
              id="add-flight-btn"
              disabled={disabled}
            >
              Add Flight
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const useFlightsInput = ({ transaction = {}, onSave }) => {
  /* **** DATA STRUCTURE ****
      trip: {
        name: string,
        employee: string (id),
        value: number,
        flights: [
          {
            date: string,
            route: {
              destinations: [
                {name: string, airport: {airportObj}}
              ],
              legs: [
                {to: string, from: string, distance: number, passClass: string}
              ]
            }
          }
        ],
      }
    */
  const { emissionData, tonsCo2e } = transaction;
  const { flights: existingFlights } = emissionData || {};

  const handleExistingFlightsWithoutRoute = (flights) =>
    flights.map((flight) => {
      const { route, destination, legs } = flight;

      if (route) {
        return flight;
      }

      return {
        ...flight,
        route: {
          destinations: destination || [{}, {}],
          legs: legs || [basePassClassObj]
        }
      };
    });

  const setInitialTripType = () => {
    if (!existingFlights) {
      return "round-trip";
    }

    if (existingFlights.length === 1) {
      return "one-way";
    }

    if (existingFlights.length === 2) {
      return "round-trip";
    }

    return "multi-city";
  };

  const [tripType, setTripType] = useState(setInitialTripType());
  const [flights, setFlights] = useState(
    existingFlights
      ? handleExistingFlightsWithoutRoute(existingFlights)
      : makeFlightArrays().roundTripFlights
  );
  const [activeFlightIdx, setActiveFlightIdx] = useState(0);
  const [flightTonsCo2e, setFlightTonsCo2e] = useState(
    existingFlights ? tonsCo2e : 0
  );

  useEffect(() => {
    setFlightTonsCo2e(totalFlightCarbonTons(flights));
  }, [flights]);

  useEffect(() => {
    if (existingFlights) {
      return setFlights(handleExistingFlightsWithoutRoute(existingFlights));
    }
  }, [existingFlights]);

  const areFlightsAcceptable = () => {
    if (!flights.length) {
      return false;
    }

    return flights.reduce((acc, { legs = [], date }) => {
      if (!date) {
        return acc && false;
      }
      const areAllLegsCompleted = legs.reduce(
        (legAcc, { passClass, defaultClass, distance }) =>
          (!!passClass || !!defaultClass) && !!distance && legAcc,
        true
      );
      if (!areAllLegsCompleted) {
        return acc && false;
      }
      return acc && true;
    }, true);
  };

  const flightInputProps = {
    transaction,
    tripType,
    setTripType,
    flights,
    setFlights,
    activeFlightIdx,
    setActiveFlightIdx
  };

  const onTransactionSave = () => {
    const emissionDate = flights[flights.length - 1].date;

    return onSave({
      tonsCo2e: flightTonsCo2e,
      date: emissionDate,
      emissionData: {
        flights
      }
    });
  };

  return {
    inputBlock: <FlightsInput {...flightInputProps} />,
    onTransactionSave,
    saveEnabled: areFlightsAcceptable(),
    tonsCo2e: flightTonsCo2e
  };
};
export default useFlightsInput;
